import { useMemo } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { CHAINFLIP_BLOCK_TIME_SECONDS } from '@chainflip/utils/consts';
import { secondsInDay, secondsInYear } from 'date-fns/constants';
import { type GetAllIncentiveSeasonsQuery } from '@/shared/graphql/generated/lp/graphql';
import { getAllIncentiveSeasonsQuery } from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import { chainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';
import useSettingsStore from './useSettingsStore';

type SeasonNode = NonNullable<GetAllIncentiveSeasonsQuery['allIncentiveSeasons']>['nodes'][number];

type TransformedSeason = {
  isActive: boolean;
  seasonIndex: number;
  endDate: Date;
  incentivisedBorrowAssets: ChainflipAsset[];
  incentivisedSupplyAssets: ChainflipAsset[];
  pools: {
    programId: number;
    asset: ChainflipAsset;
    type: 'BORROW' | 'SUPPLY';
    apy: number;
    dailyPoints: number;
    totalPoints: number;
    totalFlipAllocated: TokenAmount;
  }[];
};

const DEFAULT_INCENTIVE_PROGRAM_POSITION_VALUE_USD = 100;

const transformSeason = (
  season: SeasonNode,
  lendingPoolsData: NonNullable<ReturnType<typeof useLendingPools>['lendingPools']>,
  flipPrice: number | undefined,
): TransformedSeason => ({
  isActive: season.isActive,
  seasonIndex: season.seasonIndex ?? 0,
  endDate: new Date(season.endBlockDate),
  incentivisedBorrowAssets: [
    ...new Set(
      season.program.nodes
        .filter((program) => program.type === 'TOTAL_BORROWED_AMOUNT')
        .map((program) => program.lendingPool.asset as ChainflipAsset),
    ),
  ],
  incentivisedSupplyAssets: [
    ...new Set(
      season.program.nodes
        .filter((program) => program.type === 'TOTAL_SUPPLIED_AMOUNT')
        .map((program) => program.lendingPool.asset as ChainflipAsset),
    ),
  ],
  pools: season.program.nodes.map(
    ({ id, intervalBlocks, lendingPool, type, pointsPerInterval, totalFlip, totalPoints }) => {
      const pointsPerBlock = Number(pointsPerInterval) / intervalBlocks;
      const totalFlipAllocated = TokenAmount.fromWholeUnits(totalFlip, 'Flip');
      const flipRewardPerBlock = totalFlipAllocated.div(Number(totalPoints)).mul(pointsPerBlock);
      const flipRewardPerBlockValueUsd = flipRewardPerBlock.toUsdNumber(flipPrice);

      const totalSuppliedValueUsd = Number(
        lendingPoolsData.find((pool) => pool.asset === lendingPool.asset)?.totalAmountUsd ?? 0,
      );
      const totalBorrowedValueUsd = Number(
        lendingPoolsData.find((pool) => pool.asset === lendingPool.asset)?.totalBorrowedAmountUsd ??
          0,
      );

      const totalDenominator = Math.max(
        type === 'TOTAL_BORROWED_AMOUNT' ? totalBorrowedValueUsd : totalSuppliedValueUsd,
        DEFAULT_INCENTIVE_PROGRAM_POSITION_VALUE_USD, // provide a floor value to show rewards apy even when no positions exist
      );

      const apy =
        ((flipRewardPerBlockValueUsd * (secondsInYear / CHAINFLIP_BLOCK_TIME_SECONDS)) /
          totalDenominator) *
        100;

      return {
        programId: id,
        asset: lendingPool.asset as ChainflipAsset,
        type: type === 'TOTAL_BORROWED_AMOUNT' ? ('BORROW' as const) : ('SUPPLY' as const),
        apy,
        dailyPoints: pointsPerBlock * (secondsInDay / CHAINFLIP_BLOCK_TIME_SECONDS),
        totalPoints: Number(totalPoints),
        totalFlipAllocated,
      };
    },
  ),
});

export const useIncentives = () => {
  const { rewardsSeasonIndex } = useSettingsStore();

  const { data: seasonsData, isLoading } = useGqlQuery(getAllIncentiveSeasonsQuery, {
    context: { clientName: 'lpProcessor' },
    refetchInterval: 30_000,
    select: (data) => {
      const allSeasons = data?.allIncentiveSeasons?.nodes ?? [];
      const activeSeason = allSeasons.find((season) => season.isActive) ?? null;
      const selectedSeason =
        typeof rewardsSeasonIndex === 'number' &&
        rewardsSeasonIndex !== 0 &&
        activeSeason?.seasonIndex !== rewardsSeasonIndex
          ? (allSeasons.find((season) => season.seasonIndex === rewardsSeasonIndex) ?? null)
          : activeSeason;
      const now = Date.now();
      const availableSeasonIndices = allSeasons
        .filter((season) => new Date(season.startBlockDate).getTime() <= now)
        .map((season) => season.seasonIndex ?? 0);

      return {
        activeSeason,
        selectedSeason,
        availableSeasonIndices,
      };
    },
  });

  const rawActiveSeason = seasonsData?.activeSeason ?? null;
  const rawSelectedSeason = seasonsData?.selectedSeason ?? null;
  const availableSeasonIndices = seasonsData?.availableSeasonIndices ?? [];

  const lendingPools = useLendingPools();
  const { price: flipPrice } = useTokenPrice(chainflipAssetMap.Flip);

  const activeSeason = useMemo(
    () =>
      rawActiveSeason
        ? transformSeason(rawActiveSeason, lendingPools.lendingPools, flipPrice)
        : null,
    [rawActiveSeason, lendingPools.lendingPools, flipPrice],
  );

  const selectedSeason = useMemo(
    () =>
      rawSelectedSeason
        ? transformSeason(rawSelectedSeason, lendingPools.lendingPools, flipPrice)
        : null,
    [rawSelectedSeason, lendingPools.lendingPools, flipPrice],
  );

  return {
    activeSeason,
    selectedSeason,
    availableSeasonIndices,
    isLoading: isLoading || lendingPools.isLoading,
  };
};
